import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../cart/CartContext';
import { useSiteSettings } from '../SiteSettingsContext';
import PublicImage from '../PublicImage';
import QtyStepper from '../checkout/QtyStepper';
import { formatINR } from '../checkout/formatINR';
import { lineMaxQty, cartLineKey } from '../cart/cartUtils';
import { productDetailPath } from '../productPaths';
import FreeShippingNudge from './FreeShippingNudge';

export default function CartDrawer({ shippingRules = null }) {
  const { lines, subtotal, itemCount, drawerOpen, closeDrawer, setQty, removeItem } = useCart();
  const { settings: site } = useSiteSettings();
  const store = site.store_copy || {};
  const panelRef = useRef(null);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('cart-drawer-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('cart-drawer-open');
    };
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (drawerOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [drawerOpen]);

  if (!drawerOpen) return null;

  return (
    <div className="cart-drawer" role="presentation">
      <button type="button" className="cart-drawer__backdrop" aria-label="Close cart" onClick={closeDrawer} />
      <aside
        ref={panelRef}
        className="cart-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping bag"
        tabIndex={-1}
      >
        <header className="cart-drawer__head">
          <h2 className="cart-drawer__title">Your bag ({itemCount})</h2>
          <button type="button" className="btn-close cart-drawer__close" aria-label="Close" onClick={closeDrawer} />
        </header>

        {lines.length === 0 ? (
          <div className="cart-drawer__empty">
            <p className="text-muted mb-3">{store.cart_empty_lead_default || 'Your bag is empty.'}</p>
            <Link to="/shop" className="btn btn-primary rounded-pill w-100" onClick={closeDrawer}>
              Shop collections
            </Link>
          </div>
        ) : (
          <>
            <FreeShippingNudge subtotal={subtotal} rules={shippingRules} className="cart-drawer__shipping" />
            <ul className="cart-drawer__list list-unstyled">
              {lines.map((line) => {
                const maxQty = lineMaxQty(line.stockQty);
                return (
                  <li key={cartLineKey(line.categorySlug, line.productId, line.customization)} className="cart-drawer__line">
                    <Link
                      to={productDetailPath({ category: line.categorySlug, id: line.productId })}
                      className="cart-drawer__thumb-link"
                      onClick={closeDrawer}
                    >
                      {line.image ? (
                        <PublicImage src={line.image} alt="" className="cart-drawer__thumb" />
                      ) : (
                        <span className="cart-drawer__thumb cart-drawer__thumb--empty" aria-hidden />
                      )}
                    </Link>
                    <div className="cart-drawer__line-body">
                      <Link
                        to={productDetailPath({ category: line.categorySlug, id: line.productId })}
                        className="cart-drawer__name"
                        onClick={closeDrawer}
                      >
                        {line.name}
                      </Link>
                      <p className="cart-drawer__price small mb-1">{formatINR(line.price)}</p>
                      <QtyStepper
                        size="sm"
                        value={line.qty}
                        min={1}
                        max={maxQty}
                        onChange={(n) => setQty(line.categorySlug, line.productId, n, line.customization)}
                        label={`Quantity for ${line.name}`}
                      />
                    </div>
                    <button
                      type="button"
                      className="cart-drawer__remove btn btn-link btn-sm"
                      aria-label={`Remove ${line.name}`}
                      onClick={() => removeItem(line.categorySlug, line.productId, line.customization)}
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
            <footer className="cart-drawer__foot">
              <div className="cart-drawer__subtotal">
                <span>Subtotal</span>
                <strong>{formatINR(subtotal)}</strong>
              </div>
              <Link
                to="/checkout"
                className="btn btn-primary btn-lg rounded-pill w-100 store-cta"
                onClick={closeDrawer}
              >
                Checkout
              </Link>
              <Link to="/cart" className="cart-drawer__full-cart" onClick={closeDrawer}>
                View full cart
              </Link>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}
